// LivePip — janela flutuante 320x180 (16:9) com o canal ao vivo, no
// canto inferior direito.
//
// ⚠️ Player INDEPENDENTE (não reusa o singleton do LivePlayerProvider).
// Razão: caso de uso principal é "espiar enquanto assiste outro canal"
// (user no /player/X recebe lembrete do Y, abre PiP do Y). Singleton
// só tem 1 <video> HTML — não consegue tocar 2 streams.
//
// Trade-off: 2 streams = 2x banda/CPU. Em vlow Tizen 2017 pode degradar
// se user mantiver PiP aberto + watching algo pesado. Auto-close em
// 10/20min limita exposição. Em desktop/mid+ é tranquilo.
//
// Fluxo principal "espiar → assistir":
//   1. Toast de lembrete dispara em qualquer rota
//   2. User clica "Espiar" → openPip(channelId, group)
//   3. <LivePip> monta seu player (createPlayer + load + play, muted)
//   4. User vê o que está rolando. Opções:
//      - EXPANDIR → destrói PiP + navigate('/player/{id}').
//        PlayerScreen monta o singleton (sofre ~1-3s reload — trade-off
//        aceitável pra ter PiP independente).
//      - MUTE/UNMUTE: video.muted = !muted
//      - X: destrói player + pipStore.close()

import { detectPlatform } from '@app-iptv/device'
import { useCapability } from '@app-iptv/device/react'
import { useTranslation } from '@app-iptv/i18n'
import type { MediaPlayer } from '@app-iptv/player'
import { createPlayer } from '@app-iptv/player'
import { Focusable, getCurrentFocusKey, setFocus } from '@app-iptv/ui'
import { useEffect, useRef, useState } from 'react'

import { notifyPlaybackStarted } from '../net-priority'
import { getWarnPhase, usePipStore } from '../pip-store'
import { router } from '../routes'
import { signStreamDeduped } from '../stream-sign'
import { rewriteStreamUrl } from '../stream-url'

import styles from './LivePip.module.css'
import { IconClose, IconFullscreen, IconVolumeOff, IconVolumeOn } from './PlayerIcons'

const TICK_INTERVAL_MS = 1000

export function LivePip() {
  const { t } = useTranslation()
  const channelId = usePipStore((s) => s.channelId)
  const group = usePipStore((s) => s.group)
  const openedAt = usePipStore((s) => s.openedAt)
  const extended = usePipStore((s) => s.extended)
  const muted = usePipStore((s) => s.muted)
  const close = usePipStore((s) => s.close)
  const keepOpen = usePipStore((s) => s.keepOpen)
  const toggleMute = usePipStore((s) => s.toggleMute)
  const tick = usePipStore((s) => s.tick)
  const internalTick = usePipStore((s) => s._internalTick)

  const tier = useCapability('cpuTier')
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<MediaPlayer | null>(null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle')

  // Mount: cria player próprio + carrega canal + autoplay muted.
  useEffect(() => {
    if (!channelId || !group) return
    const video = videoRef.current
    if (!video) return

    let cancelled = false
    setStatus('loading')

    const player = createPlayer(tier)
    playerRef.current = player
    player.attach(video)
    video.muted = true // default — PiP nunca interrompe áudio existente.

    player.on('ready', () => {
      if (cancelled) return
      void player.play()
    })
    player.on('playing', () => {
      // O guard vem PRIMEIRO: `notifyPlaybackStarted()` é global (fecha a janela
      // crítica de QUEM QUER QUE esteja bufferizando). Um 'playing' atrasado de
      // um PiP já destruído cancelaria o hold do player principal que ainda
      // espera o 1º frame — soltando a telemetria dentro do buffering dele.
      if (cancelled) return
      // 1º FRAME = fim do caminho crítico de rede: libera a telemetria
      // represada desde o sign (net-priority). O PiP É playback de verdade
      // (preview tocando na tela), então abre janela crítica como os demais
      // players — e, como eles, precisa fechá-la ao pintar o 1º frame.
      notifyPlaybackStarted()
      setStatus('playing')
    })
    player.on('error', (err) => {
      if (cancelled) return

      console.warn('[pip-player] error', err)
      setStatus('error')
    })

    // Pega 1ª source do grupo (channelId tem que bater com alguma source).
    const source = group.sources.find((s) => s.channelId === channelId) ?? group.sources[0]
    if (!source) {
      setStatus('error')
      return
    }

    void signStreamDeduped('live', source.channelId)
      .then((sign) => {
        if (cancelled) return
        const fmt: 'hls' | 'mpegts' | 'mp4' =
          sign.format === 'hls'
            ? 'hls'
            : sign.format === 'mp4'
              ? 'mp4'
              : 'mpegts'
        const finalUrl = rewriteStreamUrl(sign.url, fmt, sign)
        void player.load({ url: finalUrl, type: fmt })
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      try {
        player.destroy()
      } catch {
        // ignora
      }
      playerRef.current = null
    }
  }, [channelId, group, tier])

  // Sync mute toggle com <video> próprio.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.muted = muted
  }, [muted])

  // Tick 1s pra alimentar countdown + checar auto-close.
  useEffect(() => {
    if (!channelId) return
    const id = setInterval(internalTick, TICK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [channelId, internalTick])

  // Lembra o foco anterior pra ESC/BACK devolver foco pra tela
  // sem fechar o PiP. User pode continuar usando a app, e clicar
  // tecla Yellow (ou 'Y' dev) pra voltar foco pro PiP a qualquer momento.
  const previousFocusKeyRef = useRef<string | null>(null)

  // ESC/BACK devolve foco pra tela (NÃO fecha PiP). User só fecha
  // explicitamente via botão X. Tecla Yellow (ColorF2Yellow keyCode
  // 405, ou 'Y' dev) re-foca PiP.
  // Capture phase + addEventListener com {capture:true} pra pegar
  // antes de outros listeners (usePlayerKeys, useTvKeys, etc) que
  // podem dar preventDefault ou consumir o event.
  useEffect(() => {
    if (!channelId) return
    const onKey = (e: KeyboardEvent) => {
      const focusedKey = getCurrentFocusKey()
      const isInPip = typeof focusedKey === 'string' && focusedKey.startsWith('pip-')
      const inInput = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement

      if (e.key === 'Escape' && isInPip) {
        e.preventDefault()
        if (previousFocusKeyRef.current) {
          setFocus(previousFocusKeyRef.current)
        }
        return
      }

      const isYellowKey =
        e.key === 'ColorF2Yellow' ||
        e.keyCode === 405 ||
        ((e.key === 'y' || e.key === 'Y') && !inInput)
      if (isYellowKey) {
        e.preventDefault()
        e.stopPropagation()
        if (!isInPip) {
          previousFocusKeyRef.current = typeof focusedKey === 'string' ? focusedKey : null
        }
        setFocus('pip-expand')
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [channelId])

  // Auto-foco no PiP quando abre. Retry pattern porque Norigin precisa
  // do Focusable já registrado (próximo frame após mount). Guarda foco
  // anterior pra ESC poder devolver.
  useEffect(() => {
    if (!channelId) return
    const prev = getCurrentFocusKey()
    previousFocusKeyRef.current = typeof prev === 'string' ? prev : null
    let tries = 0
    const tryFocus = () => {
      setFocus('pip-expand')
      if (tries++ < 8) setTimeout(tryFocus, 80)
    }
    requestAnimationFrame(tryFocus)
  }, [channelId])

  if (!channelId || !group) return null

  void tick
  const { phase, secondsUntilClose } = getWarnPhase(openedAt, extended)

  // Hint visual quando foco está FORA do PiP. Tick 1×/s re-renderiza
  // este componente — reavalia foco a cada tick.
  // Hint = glow sutil na borda (amarelo em TV nativa, branco em web).
  // Mobile/touch: sem hint (usuário toca direto).
  const focusedKey = getCurrentFocusKey()
  const focusInPip = typeof focusedKey === 'string' && focusedKey.startsWith('pip-')
  const platform = detectPlatform()
  const showHint = !focusInPip && platform !== 'mobile-pwa'
  const hintPlatform = platform === 'web' ? 'web' : 'tv'

  const handleExpand = () => {
    close()
    void router.navigate(`/player/${channelId}`)
  }

  return (
    <div className={styles.livePip} role="region" aria-label={group.displayName}>
      {showHint && (
        <div className={styles.livePipHintInline} aria-hidden="true">
          <span className={styles.livePipHintInlineDot} />
          <span>{hintPlatform === 'tv' ? t('pip.hintYellow') : t('pip.hintKeyboard')}</span>
        </div>
      )}
      <div
        className={styles.livePipFrame}
        data-needs-focus-hint={showHint ? 'true' : undefined}
        data-platform={hintPlatform}
      >
      <div className={styles.livePipVideoBox}>
        <video ref={videoRef} playsInline autoPlay muted />
        {status === 'loading' && (
          <div className={styles.livePipLoading} aria-hidden="true" />
        )}
        <div className={styles.livePipControls}>
          <Focusable
            focusKey="pip-expand"
            onEnterPress={handleExpand}
            onArrowPress={(dir) => {
              if (dir === 'right') {
                setFocus('pip-mute')
                return false
              }
              return false
            }}
            className={styles.livePipExpandBtn}
          >
            <IconFullscreen />
            <span>{t('pip.expand')}</span>
          </Focusable>
          <div className={styles.livePipIconBtns}>
            <Focusable
              focusKey="pip-mute"
              onEnterPress={toggleMute}
              onArrowPress={(dir) => {
                if (dir === 'left') {
                  setFocus('pip-expand')
                  return false
                }
                if (dir === 'right') {
                  setFocus('pip-close')
                  return false
                }
                return false
              }}
              className={styles.livePipIconBtn}
              aria-label={muted ? t('pip.unmute') : t('pip.mute')}
            >
              {muted ? <IconVolumeOff /> : <IconVolumeOn />}
            </Focusable>
            <Focusable
              focusKey="pip-close"
              onEnterPress={close}
              onArrowPress={(dir) => {
                if (dir === 'left') {
                  setFocus('pip-mute')
                  return false
                }
                return false
              }}
              className={styles.livePipIconBtn}
              aria-label={t('pip.close')}
            >
              <IconClose />
            </Focusable>
          </div>
        </div>
      </div>

      {phase !== 'none' && (
        <div className={styles.livePipWarn} role="status" aria-live="polite">
          <span className={styles.livePipWarnText}>
            {t('pip.closingIn', { seconds: secondsUntilClose })}
          </span>
          {phase === 'first-warn' && (
            <Focusable
              focusKey="pip-keep"
              onEnterPress={keepOpen}
              className={styles.livePipKeepBtn}
            >
              {t('pip.keepOpen')}
            </Focusable>
          )}
        </div>
      )}
      </div>
    </div>
  )
}
